/**
 * A* on the 4-directional tile grid.
 *
 * The client runs it on click to turn a destination into a path; the server
 * runs the same code to decide what path a player is actually allowed to walk.
 * Same inputs, same output, so the two never disagree about what is legal.
 */
/** Manhattan distance: the exact cost of the cheapest 4-directional path on an open grid. */
function heuristic(ax, ay, bx, by) {
    return Math.abs(ax - bx) + Math.abs(ay - by);
}
/**
 * Binary min-heap over (tile index, f-score), kept in two parallel typed arrays
 * so a pop touches no allocation. It starts small and doubles when full: a
 * short walk on the 384×384 world queues a few dozen tiles, and sizing it for
 * the whole grid (four entries per tile, 7 MB) on every call was most of what
 * a server tick cost, garbage collection included.
 */
class NodeQueue {
    indices = new Int32Array(1024);
    scores = new Float64Array(1024);
    count = 0;
    clear() {
        this.count = 0;
    }
    get size() {
        return this.count;
    }
    swap(a, b) {
        const index = this.indices[a];
        const score = this.scores[a];
        this.indices[a] = this.indices[b];
        this.scores[a] = this.scores[b];
        this.indices[b] = index;
        this.scores[b] = score;
    }
    push(index, score) {
        if (this.count === this.indices.length)
            this.grow();
        let child = this.count;
        this.indices[child] = index;
        this.scores[child] = score;
        this.count += 1;
        while (child > 0) {
            const parent = (child - 1) >> 1;
            if (this.scores[parent] <= this.scores[child])
                break;
            this.swap(parent, child);
            child = parent;
        }
    }
    grow() {
        const indices = new Int32Array(this.indices.length * 2);
        const scores = new Float64Array(this.scores.length * 2);
        indices.set(this.indices);
        scores.set(this.scores);
        this.indices = indices;
        this.scores = scores;
    }
    /** Lowest-score index, or -1 when empty. */
    pop() {
        if (this.count === 0)
            return -1;
        const top = this.indices[0];
        this.count -= 1;
        if (this.count > 0) {
            this.indices[0] = this.indices[this.count];
            this.scores[0] = this.scores[this.count];
            let parent = 0;
            for (;;) {
                const left = parent * 2 + 1;
                const right = left + 1;
                let smallest = parent;
                if (left < this.count && this.scores[left] < this.scores[smallest])
                    smallest = left;
                if (right < this.count && this.scores[right] < this.scores[smallest])
                    smallest = right;
                if (smallest === parent)
                    break;
                this.swap(parent, smallest);
                parent = smallest;
            }
        }
        return top;
    }
}
const scratches = new Map();
function scratchFor(size) {
    let scratch = scratches.get(size);
    if (scratch === undefined) {
        scratch = {
            stamp: 0,
            seen: new Uint32Array(size),
            closed: new Uint32Array(size),
            gScore: new Float64Array(size),
            cameFrom: new Int32Array(size),
            queue: new NodeQueue(),
        };
        scratches.set(size, scratch);
    }
    scratch.stamp += 1;
    // After four billion searches the stamps wrap: start the arrays afresh.
    if (scratch.stamp === 0xffffffff) {
        scratch.seen.fill(0);
        scratch.closed.fill(0);
        scratch.stamp = 1;
    }
    scratch.queue.clear();
    return scratch;
}
/**
 * Shortest 4-directional path from `start` to `goal`, both included.
 *
 * Returns null when the goal is unreachable, blocked, or off the map. A start
 * equal to the goal yields a single-tile path, which callers read as "already
 * there" rather than as a failure.
 */
export function findPath(grid, start, goal, options = {}) {
    if (!grid.isWalkableTile(start) || !grid.isWalkableTile(goal))
        return null;
    if (start.x === goal.x && start.y === goal.y)
        return [{ x: start.x, y: start.y }];
    const isBlocked = options.isBlocked;
    if (isBlocked?.(goal) === true)
        return null;
    const { width, height } = grid;
    const size = width * height;
    const maxExpanded = options.maxExpanded ?? size;
    const startIndex = start.y * width + start.x;
    const goalIndex = goal.y * width + goal.x;
    const { stamp, seen, closed, gScore, cameFrom, queue } = scratchFor(size);
    seen[startIndex] = stamp;
    gScore[startIndex] = 0;
    cameFrom[startIndex] = -1;
    queue.push(startIndex, heuristic(start.x, start.y, goal.x, goal.y));
    let expanded = 0;
    while (queue.size > 0) {
        const current = queue.pop();
        if (current === goalIndex)
            return reconstruct(cameFrom, current, width);
        if (closed[current] === stamp)
            continue;
        closed[current] = stamp;
        expanded += 1;
        if (expanded > maxExpanded)
            return null;
        const cx = current % width;
        const cy = (current - cx) / width;
        const tentative = gScore[current] + 1;
        for (const neighbour of grid.neighbours(cx, cy)) {
            const index = neighbour.y * width + neighbour.x;
            if (closed[index] === stamp)
                continue;
            if (seen[index] === stamp && tentative >= gScore[index])
                continue;
            if (isBlocked?.(neighbour) === true)
                continue;
            seen[index] = stamp;
            cameFrom[index] = current;
            gScore[index] = tentative;
            queue.push(index, tentative + heuristic(neighbour.x, neighbour.y, goal.x, goal.y));
        }
    }
    return null;
}
function reconstruct(cameFrom, goalIndex, width) {
    const path = [];
    let index = goalIndex;
    while (index !== -1) {
        const x = index % width;
        path.push({ x, y: (index - x) / width });
        index = cameFrom[index];
    }
    return path.reverse();
}
/** True when `b` is orthogonally adjacent to `a`. The only legal single step. */
export function isAdjacentStep(a, b) {
    return Math.abs(a.x - b.x) + Math.abs(a.y - b.y) === 1;
}
//# sourceMappingURL=pathfinding.js.map